import React, { useState, useEffect } from 'react';
import Dialog from '@components/Atoms/Dialog';
import Typography from '@components/Atoms/Typography';
import TextField from '@components/Atoms/TextField';
import Box from '@components/Atoms/Box';
import { LockStrategy } from '../../types';
import LockStrategyRadio from './LockStrategyRadio';

const UnlockConfirmationDialog = ({
  onCancel,
  onConfirm,
  open,
  setAutoLockDuration,
}: {
  onCancel: () => void;
  onConfirm: (lockStrategy: LockStrategy) => void;
  setAutoLockDuration: (autoLockDuration: number) => void;
  open: boolean;
}) => {
  const [lockStrategy, setLockStrategy] = useState<LockStrategy>(LockStrategy.USE_REMAINING_TIME);
  const [days, setDays] = useState(1);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);

  // Convert days, hours, minutes back to total minutes
  const updateTotalMinutes = (newDays: number, newHours: number, newMinutes: number) => {
    const totalMinutes = newDays * 24 * 60 + newHours * 60 + newMinutes;
    setAutoLockDuration(totalMinutes);
  };

  useEffect(() => {
    updateTotalMinutes(days, hours, minutes);
  }, [days, hours, minutes]);

  return (
    <Dialog
      actions={[
        {
          buttonProps: {
            variant: 'outlined',
            dataTestId: 'cancel-exam-action-dialog',
          },
          label: 'Cancel',
          onClick: onCancel,
        },
        {
          buttonProps: {
            variant: 'contained',
            dataTestId: 'confirm-exam-action-dialog',
            color: 'play',
          },
          label: 'Unlock',
          onClick: () => onConfirm(lockStrategy),
        },
      ]}
      open={open}
      onClose={onCancel}
      data-testid="exam-action-dialog"
      title="Unlock Exam"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          sx={{
            textAlign: 'start',
          }}
          variant="body2"
          fontWeight={500}
        >
          Are you sure you want to unlock the exam?
        </Typography>
        <Typography variant="body1">Auto Lock window duration</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            data-testid='unlock-days-input'
            label="Days"
            value={days}
            onChange={(e) => {
              const newDays = Number(e.target.value);
              setDays(newDays);
            }}
            variant="outlined"
            type="number"
            inputProps={{
              min: 0,
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            data-testid="unlock-hours-input"
            label="Hours"
            value={hours}
            onChange={(e) => {
              const newHours = Number(e.target.value);
              setHours(newHours);
            }}
            variant="outlined"
            type="number"
            inputProps={{
              min: 0,
              max: 23,
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            data-testid="unlock-minutes-input"
            label="Minutes"
            value={minutes}
            onChange={(e) => {
              const newMinutes = Number(e.target.value);
              setMinutes(newMinutes);
            }}
            variant="outlined"
            type="number"
            inputProps={{
              min: 0,
              max: 59,
            }}
            sx={{ flex: 1 }}
          />
        </Box>
        <LockStrategyRadio
          lockStrategy={lockStrategy}
          setLockStrategy={setLockStrategy}
        />
      </Box>
    </Dialog>
  );
};

export default UnlockConfirmationDialog;
